import React, { useContext } from 'react';
import ReactTooltip from 'react-tooltip';
import styled from 'styled-components';
import { Experience } from '@/webapi/use-experience-api';
import {
  PriceTestProductSummary,
  PriceTestProductVariation,
} from '@/features/details/price-test-reports/api';
import { getGoal, MetricKind } from '@/webapi/models';
import {
  format,
  getUplift,
  getVariantName,
  SummaryBody,
  SummaryHeader,
} from '@/features/details/price-test-reports/shared';
import { Tooltip } from '@/components/tooltip';
import { HSpace, VSpace } from '@/components/spacing';
import { Uplift } from '@/features/details/summary/uplift-badge';
import {
  CIToolTip,
  ConfidenceIntervals,
} from '@/features/details/summary/confidence-interval';
import { currencySymbol } from '@/utils/currencies';
import {
  PriceOffsetResponse,
  PriceTestReportsContext,
} from '@/features/details/price-test-reports/context';

function PriceOffset({
  priceOffsets,
  pid,
  useCaseVariant,
}: {
  useCaseVariant: string;
  pid: number;
  priceOffsets: PriceOffsetResponse;
}) {
  const symbol = currencySymbol();

  const offset = priceOffsets?.list
    ?.find((o) => o.variantId === useCaseVariant)
    ?.list?.find((p) => p.pid === `${pid}`);
  if (!offset || !offset.OffsetType || !offset.Offset) {
    return null;
  }
  const abs = Math.abs(offset?.Offset);
  const sign = Math.sign(offset.Offset) ? `+` : `-`;
  return (
    <PriceOffsetGrid>
      (
      {offset?.OffsetType === `fixed`
        ? `${sign}${symbol}${abs}`
        : `${sign}${abs}%`}
      )
    </PriceOffsetGrid>
  );
}
const PriceOffsetGrid = styled.p`
  margin: 0.5rem 0 0 0;
  padding: 0;
  font-weight: normal;
  font-size: 1.2rem;
`;

export function DesktopStats({
  experience,
  p,
}: {
  experience?: Experience;
  p?: PriceTestProductSummary;
}) {
  const symbol = currencySymbol();
  const { primaryGoal, priceOffsets } = useContext(PriceTestReportsContext);

  return (
    <>
      <DeskMetricsGrid>
        <SummaryHeader>Variation</SummaryHeader>
        <SummaryHeader>
          Sessions
          <Tooltip
            zIndex={0}
            title="Sessions"
            text="sessions made by users that visited the tested product"
          />
        </SummaryHeader>
        <SummaryHeader>
          Product CR
          <Tooltip
            zIndex={0}
            title="Product CR"
            text="conversion rate for sessions that visit the tested product"
          />
        </SummaryHeader>
        <SummaryHeader>
          Product PSV
          <Tooltip
            zIndex={0}
            title="Product PSV"
            text="per session value for sessions that visit the tested product"
          />
        </SummaryHeader>
        <SummaryHeader>
          Units Sold
          <Tooltip
            zIndex={0}
            title=" Units Sold"
            text="the total number of items sold"
          />
        </SummaryHeader>
        <SummaryHeader>
          Transactions
          <Tooltip
            zIndex={0}
            title="Transactions"
            text="the number of complete purchases with at least 1 item from the tested products"
          />
        </SummaryHeader>
        <SummaryHeader>
          OTW
          <Tooltip
            zIndex={0}
            title="Odds To Win"
            text="The probability Version A is Better than Version B. Based on the primary metric."
          />
        </SummaryHeader>
        <SummaryHeader>
          CI
          <CIToolTip />
        </SummaryHeader>
        {p?.variants.map((v) => (
          <React.Fragment key={v.useCaseVariant}>
            <SummaryBody col>
              <span>{getVariantName(experience, v)}</span>
              <PriceOffset
                useCaseVariant={v.useCaseVariant}
                pid={p.pid}
                priceOffsets={priceOffsets}
              />
            </SummaryBody>
            <SummaryBody>
              <GridValue
                variation={v}
                pid={p.pid}
                metric={MetricKind.PRODUCT_SESSIONS}
                value={format(v.sessions)}
                uplift={getUplift(v, p, (v) => v.sessions)}
              />
            </SummaryBody>
            <SummaryBody>
              <GridValue
                pid={p.pid}
                variation={v}
                metric={MetricKind.PRODUCT_CR}
                value={format((v.transactions / v.sessions) * 100, ``, `%`)}
                uplift={getUplift(v, p, (v) => v.transactions / v.sessions)}
              />
            </SummaryBody>
            <SummaryBody>
              <GridValue
                pid={p.pid}
                variation={v}
                metric={MetricKind.PRODUCT_PSV}
                value={format(v.psv, symbol)}
                uplift={getUplift(v, p, (v) => v.psv)}
              />
            </SummaryBody>
            <SummaryBody>
              <GridValue
                pid={p.pid}
                variation={v}
                metric={MetricKind.PRODUCT_ITEMS_SOLD}
                value={format(v.itemsSold)}
                uplift={getUplift(v, p, (v) => v.itemsSold)}
              />
            </SummaryBody>
            <SummaryBody>
              <GridValue
                pid={p.pid}
                variation={v}
                metric={MetricKind.PRODUCT_TRANSACTIONS}
                value={format(v.transactions)}
                uplift={getUplift(v, p, (v) => v.transactions)}
              />
            </SummaryBody>
            <SummaryBody>
              <span>{format(v.otw * 100, ``, `%`)}</span>
            </SummaryBody>
            <ConfidenceIntervals
              variant={
                {
                  id: v.useCaseVariant,
                  name: getVariantName(experience, v),
                } as any
              }
              uplift={getUplift(v, p, (v) => getGoal(v, primaryGoal))}
            />
          </React.Fragment>
        ))}
      </DeskMetricsGrid>
      <VSpace value={3} />
      <DeskMetricsGrid bt>
        <SummaryHeader>Variation</SummaryHeader>
        <SummaryHeader>
          PDV %
          <Tooltip
            zIndex={0}
            title="PDV %"
            text="the percentage of sessions that reached the tested product page"
          />
        </SummaryHeader>
        <SummaryHeader>
          Profit
          <Tooltip
            zIndex={0}
            title="Profit"
            text="net profit after subtracting costs from revenue"
          />
        </SummaryHeader>
        <SummaryHeader>
          Profit %
          <Tooltip
            zIndex={0}
            title="Profit %"
            text="percentage of revenue that is profit"
          />
        </SummaryHeader>
        <SummaryHeader>
          PPS
          <Tooltip
            zIndex={0}
            title="Profit Per Session"
            text="average profit earned per session"
          />
        </SummaryHeader>
        <SummaryHeader>
          Returns
          <Tooltip
            zIndex={0}
            title="Returns"
            text="the total number of items returned"
          />
        </SummaryHeader>
        <SummaryHeader>
          IPP
          <Tooltip
            zIndex={0}
            title="Items Per Purchase"
            text="average number of items bought per transaction "
          />
        </SummaryHeader>
        <SummaryHeader>
          Revenue
          <Tooltip
            zIndex={0}
            title="Revenue"
            text="total revenue generated from the tested product"
          />
        </SummaryHeader>
        {p?.variants.map((v) => (
          <React.Fragment key={v.useCaseVariant}>
            <SummaryBody col>
              <span>{getVariantName(experience, v)}</span>
              <PriceOffset
                useCaseVariant={v.useCaseVariant}
                pid={p.pid}
                priceOffsets={priceOffsets}
              />
            </SummaryBody>
            <SummaryBody>
              <GridValue
                pid={p.pid}
                variation={v}
                metric={MetricKind.PRODUCT_DETAILS_VIEWS_RATE}
                value={format(v.pdv || 0, ``, `%`)}
                uplift={getUplift(v, p, (v) => v.pdv)}
              />
            </SummaryBody>
            <SummaryBody>
              <GridValue
                pid={p.pid}
                variation={v}
                metric={MetricKind.PRODUCT_PROFIT}
                value={format(v.profit, symbol)}
                uplift={getUplift(v, p, (v) => v.profit)}
              />
            </SummaryBody>
            <SummaryBody>
              <GridValue
                pid={p.pid}
                variation={v}
                metric={MetricKind.PRODUCT_PROFIT_PERCENT}
                value={format((v.profit / v.revenue) * 100, ``, `%`)}
                uplift={getUplift(v, p, (v) => v.profit / v.revenue)}
              />
            </SummaryBody>
            <SummaryBody>
              <GridValue
                pid={p.pid}
                variation={v}
                metric={MetricKind.PRODUCT_PROFIT_PER_SESSION}
                value={format(v.profit / v.sessions, symbol)}
                uplift={getUplift(v, p, (v) => v.profit / v.sessions)}
              />
            </SummaryBody>
            <SummaryBody>
              <GridValue
                pid={p.pid}
                variation={v}
                metric={MetricKind.PRODUCT_RETURNS}
                value={format(v.returns)}
                uplift={getUplift(v, p, (v) => v.returns)}
              />
            </SummaryBody>
            <SummaryBody>
              <GridValue
                pid={p.pid}
                variation={v}
                metric={MetricKind.PRODUCT_IPP}
                value={format(v.itemsSold / v.transactions)}
                uplift={getUplift(v, p, (v) => v.itemsSold / v.transactions)}
              />
            </SummaryBody>
            <SummaryBody>
              <GridValue
                pid={p.pid}
                variation={v}
                metric={MetricKind.PRODUCT_REVENUE}
                value={format(v.revenue, symbol)}
                uplift={getUplift(v, p, (v) => v.revenue)}
              />
            </SummaryBody>
          </React.Fragment>
        ))}
      </DeskMetricsGrid>
      <VSpace value={2} />
      <DeskMetricsGrid bt>
        <SummaryHeader>Variation</SummaryHeader>
        <SummaryHeader>
          Returns %
          <Tooltip
            zIndex={0}
            title="Retrns %"
            text="percentage of sold items that were returned"
          />
        </SummaryHeader>
        <SummaryHeader>
          ATC %
          <Tooltip
            zIndex={0}
            title="ATC %"
            text="percentage of sessions that added the product to the  cart"
          />
        </SummaryHeader>
        <HSpace />
        <HSpace />
        <HSpace />
        <HSpace />
        <HSpace />
        {p?.variants.map((v) => (
          <React.Fragment key={v.useCaseVariant}>
            <SummaryBody col>
              <span>{getVariantName(experience, v)}</span>
              <PriceOffset
                useCaseVariant={v.useCaseVariant}
                pid={p.pid}
                priceOffsets={priceOffsets}
              />
            </SummaryBody>
            <SummaryBody>
              <GridValue
                pid={p.pid}
                variation={v}
                metric={MetricKind.PRODUCT_RETURN_PERCENT}
                value={format((v.returns / v.itemsSold) * 100, ``, `%`)}
                uplift={getUplift(v, p, (v) => v.returns / v.itemsSold)}
              />
            </SummaryBody>
            <SummaryBody>
              <GridValue
                pid={p.pid}
                variation={v}
                metric={MetricKind.PRODUCT_ATC_RATE}
                value={format(v.atcRate, ``, `%`)}
                uplift={getUplift(v, p, (v) => v.atcRate)}
              />
            </SummaryBody>
            <HSpace />
            <HSpace />
            <HSpace />
            <HSpace />
            <HSpace />
          </React.Fragment>
        ))}
      </DeskMetricsGrid>
    </>
  );
}

const DeskMetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  grid-gap: 2rem;
  grid-row-gap: 3.5rem;
  border-top: ${(p) => (p.bt ? 1 : 0)}px solid rgba(216, 216, 216, 0.63);
  padding-top: ${(p) => (p.bt ? 3 : 0)}rem;
`;

export function GridValue({
  metric,
  uplift,
  value,
  variation: { isControl, useCaseVariant },
  pid,
}: {
  variation: PriceTestProductVariation;
  metric: MetricKind;
  value: string;
  uplift: number;
  pid: number;
}) {
  const { primaryGoal } = useContext(PriceTestReportsContext);
  return (
    <span
      data-tip={!isControl}
      data-for={isControl ? `` : `${metric + useCaseVariant}${pid}-tooltip`}
    >
      {value}
      {isControl ? null : (
        <>
          <Uplift value={uplift} show={primaryGoal === metric} />
          <ReactTooltip
            className="uplift-tooltip"
            place="right"
            id={`${metric + useCaseVariant}${pid}-tooltip`}
            arrowColor="transparent"
          >
            <Uplift value={uplift} show={primaryGoal !== metric} />
          </ReactTooltip>
        </>
      )}
    </span>
  );
}
